import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Layers,
    RefreshCw,
    ArrowLeft,
    AlertCircle,
    TreePine,
    IndianRupee,
    PieChart,
    TrendingUp,
} from 'lucide-react';
import Header from '../../../components/header/Header';
import Footer from '../../../components/footer/Footer';
import HeroCarousel from '../../../components/carousel/HeroCarousel';
import Breadcrumb from '../../../shared/components/Breadcrumb';
import KVSLoader from '../../../components/loaders/KVSLoader';
import ExpenditureActivityTree from '../components/ExpenditureActivityTree';
import { useGetExpenditureActivityTreeQuery } from '../services/api';
import { YearDropdown, PhaseDropdown } from '../components/filters';
import type { ExpenditureActivityTreeNode } from '../types';

import img1 from '../../../assets/pmshribanner.webp';

const ExpenditureActivityTreeScreen: React.FC = () => {
    const navigate = useNavigate();
    const [selectedNode, setSelectedNode] = useState<ExpenditureActivityTreeNode | null>(null);
    const [selectedYear, setSelectedYear] = useState<number | null>(null);
    const [selectedPhase, setSelectedPhase] = useState<number | null>(null);
    
    // Use RTK Query hook to fetch expenditure activity tree data
    const { data: response, isLoading, error, refetch } = useGetExpenditureActivityTreeQuery({
        ...(selectedYear !== null && { year: selectedYear }),
        ...(selectedPhase !== null && { phase: selectedPhase }),
    });
    const treeData = response?.data?.tree || [];

    // Carousel images
    const carouselImages = [img1];

    // Handle node click
    const handleNodeClick = useCallback((node: ExpenditureActivityTreeNode) => {
        setSelectedNode(node);
    }, []);

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        }).format(amount);
    };

    // Calculate totals
    const totalSanctioned = treeData.reduce((acc: number, node) => acc + node.sanTotal, 0);
    const totalSpent = treeData.reduce((acc: number, node) => acc + node.spentTotal, 0);
    const totalRecurring = treeData.reduce((acc: number, node) => acc + node.sanRec, 0);
    const totalNonRecurring = treeData.reduce((acc: number, node) => acc + node.sanNonRec, 0);
    // Spent breakdown
    const totalSpentRecurring = treeData.reduce((acc: number, node) => acc + node.spentRec, 0);
    const totalSpentNonRecurring = treeData.reduce((acc: number, node) => acc + node.spentNonRec, 0);
    const utilizationRate = totalSanctioned > 0 ? (totalSpent / totalSanctioned) * 100 : 0;

    return (
        <div className="min-h-screen flex flex-col bg-gradient-to-br from-gray-50 via-white to-blue-50">
            <Header />
            <HeroCarousel images={carouselImages} />
            <Breadcrumb />

            <main className="flex-grow container mx-auto px-4 py-6">
                {/* Page Header */}
                <div className="mb-6">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => navigate('/pm-shri')}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <ArrowLeft className="h-5 w-5 text-gray-600" />
                            </button>
                            <div>
                                <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 flex items-center gap-2">
                                    <TreePine className="h-7 w-7 text-green-600" />
                                    Expenditure Activity Tree {selectedYear ? `(${selectedYear}-${(selectedYear + 1).toString().slice(-2)})` : '(All Years)'}
                                </h1>
                                <p className="text-gray-600 mt-1">
                                    View sanctioned and spent amounts across the activity hierarchy
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            {/* Year Dropdown */}
                            <YearDropdown
                                value={selectedYear}
                                onChange={(val) => setSelectedYear(val)}
                            />
                            {/* Phase Dropdown */}
                            <PhaseDropdown
                                value={selectedPhase}
                                onChange={setSelectedPhase}
                            />
                            <button
                                onClick={() => refetch()}
                                disabled={isLoading}
                                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                            >
                                <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                                Refresh
                            </button>
                        </div>
                    </div>
                </div>

                {/* Financial Summary Cards */}
                {!isLoading && !error && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 flex items-center justify-between">
                            <div className="flex-1">
                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Total Sanctioned</p>
                                <p className="text-xl font-bold text-orange-600">{formatCurrency(totalSanctioned)}</p>

                                <div className="mt-3 text-sm text-gray-700">
                                    <div className="flex justify-between">
                                        <span className="text-cyan-600">Recurring</span>
                                        <span className="font-semibold">{formatCurrency(totalRecurring)}</span>
                                    </div>
                                    <div className="flex justify-between mt-1">
                                        <span className="text-rose-600">Non-Recurring</span>
                                        <span className="font-semibold">{formatCurrency(totalNonRecurring)}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="p-3 bg-orange-100 text-orange-600 rounded-full ml-4">
                                <IndianRupee className="h-6 w-6" />
                            </div>
                        </div>

                        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 flex items-center justify-between">
                            <div className="flex-1">
                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Total Spent</p>
                                <p className="text-xl font-bold text-green-600">{formatCurrency(totalSpent)}</p>

                                <div className="mt-3 text-sm text-gray-700">
                                    <div className="flex justify-between">
                                        <span className="text-cyan-600">Recurring</span>
                                        <span className="font-semibold">{formatCurrency(totalSpentRecurring)}</span>
                                    </div>
                                    <div className="flex justify-between mt-1">
                                        <span className="text-rose-600">Non-Recurring</span>
                                        <span className="font-semibold">{formatCurrency(totalSpentNonRecurring)}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="p-3 bg-green-100 text-green-600 rounded-full ml-4">
                                <IndianRupee className="h-6 w-6" />
                            </div>
                        </div>
                        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 flex items-center justify-between">
                            <div>
                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Utilization</p>
                                <p className="text-xl font-bold text-blue-600">{utilizationRate.toFixed(1)}%</p>
                            </div>
                            <div className="p-3 bg-blue-100 text-blue-600 rounded-full">
                                <TrendingUp className="h-6 w-6" />
                            </div>
                        </div>
                    
                    </div>
                )}

                {/* Error State */}
                {error && (
                    <div className="mb-6 p-4 bg-[#f3e7dc] border border-[#ecd5bb] rounded-xl flex items-center gap-3">
                        <AlertCircle className="h-5 w-5 text-[#8B4513] flex-shrink-0" />
                        <div className="flex-1">
                            <p className="font-medium text-[#8B4513]">Error Loading Data</p>
                            <p className="text-sm text-[#8B4513]">
                                {typeof error === 'object' && 'message' in error 
                                    ? (error as any).message 
                                    : 'Failed to fetch expenditure activity tree. Please try again later.'}
                            </p>
                        </div>
                        <button
                            onClick={() => refetch()}
                            className="px-3 py-1.5 bg-[#fbe9d7] text-[#8B4513] rounded-lg hover:bg-[#f5dfc2] transition-colors text-sm font-medium"
                        >
                            Retry
                        </button>
                    </div>
                )}

                {/* Loading State */}
                {isLoading && (
                    <div className="flex items-center justify-center h-96">
                        <KVSLoader />
                    </div>
                )}

                {/* Main Content */}
                {!isLoading && !error && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Activity Tree - 2 columns */}
                        <div className="lg:col-span-2">
                            <ExpenditureActivityTree
                                data={treeData}
                                isLoading={isLoading}
                                onNodeClick={handleNodeClick}
                            />
                        </div>

                        {/* Details Panel - 1 column */}
                        <div className="lg:col-span-1">
                            <div className="bg-white rounded-xl shadow-lg border border-gray-200 sticky top-4">
                                <div className="bg-gradient-to-r from-green-600 to-teal-600 px-6 py-4 rounded-t-xl">
                                    <h3 className="text-lg font-bold text-white flex items-center gap-2">
                                        <Layers className="h-5 w-5" />
                                        Activity Details
                                    </h3>
                                </div>

                                <div className="p-6">
                                    {selectedNode ? (
                                        <div className="space-y-4">
                                            {/* Title */}
                                            <div>
                                                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                                                    Title
                                                </label>
                                                <p className="text-lg font-semibold text-gray-800 mt-1">
                                                    {selectedNode.title}
                                                </p>
                                            </div>

                                            {/* Stats */}
                                            <div className="space-y-3">
                                                <div className="bg-orange-50 p-3 rounded-lg">
                                                    <p className="text-xs text-orange-700 font-semibold uppercase mb-2">Sanctioned</p>
                                                    <p className="text-2xl font-bold text-orange-600">{formatCurrency(selectedNode.sanTotal)}</p>
                                                    <div className="flex justify-between mt-2 text-sm">
                                                        <div>
                                                            <span className="text-cyan-700">Recurring</span>
                                                            <p className="font-semibold text-cyan-600">{formatCurrency(selectedNode.sanRec)}</p>
                                                        </div>
                                                        <div>
                                                            <span className="text-rose-700">Non-Recurring</span>
                                                            <p className="font-semibold text-rose-600">{formatCurrency(selectedNode.sanNonRec)}</p>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="bg-green-50 p-3 rounded-lg">
                                                    <p className="text-xs text-green-700 font-semibold uppercase mb-2">Spent</p>
                                                    <p className="text-2xl font-bold text-green-600">{formatCurrency(selectedNode.spentTotal)}</p>
                                                    <div className="flex justify-between mt-2 text-sm">
                                                        <div>
                                                            <span className="text-cyan-700">Recurring</span>
                                                            <p className="font-semibold text-cyan-600">{formatCurrency(selectedNode.spentRec)}</p>
                                                        </div>
                                                        <div>
                                                            <span className="text-rose-700">Non-Recurring</span>
                                                            <p className="font-semibold text-rose-600">{formatCurrency(selectedNode.spentNonRec)}</p>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="bg-blue-50 p-3 rounded-lg">
                                                    <p className="text-xs text-blue-700 font-semibold uppercase mb-1">Utilization</p>
                                                    <p className="text-2xl font-bold text-blue-600">
                                                        {selectedNode.sanTotal > 0 
                                                            ? ((selectedNode.spentTotal / selectedNode.sanTotal) * 100).toFixed(1) 
                                                            : '0.0'}%
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Children Count */}
                                            {selectedNode.children && selectedNode.children.length > 0 && (
                                                <div className="bg-gray-50 p-3 rounded-lg">
                                                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                                                        Children
                                                    </label>
                                                    <p className="text-lg font-semibold text-gray-800 mt-1">
                                                        {selectedNode.children.length} items
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="text-center py-12 text-gray-500">
                                            <PieChart className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                                            <p className="font-medium text-gray-600">No Activity Selected</p>
                                            <p className="text-sm mt-1">
                                                Click on any item in the tree to view details
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </main>

            <Footer />
        </div>
    );
};

export default ExpenditureActivityTreeScreen;
